/* Store module to handle todo-menu */
import Vue from 'vue';
import EventBus from '@/core/event-bus.js';

const state = {
  standardSettings: {
    deviatingCheckCredits: [],
    avgSocialFeesPercentage: 31.42,
    avgTaxPercentage: 29.4,
    costPaymentOffset: 1,
    revenuePaymentOffset: 1,
    generalCheckCredit: 0,
    periodForVatCalculation: 1,
    standardVatForCost: 25,
    standardVatForRevenue: 25,
    handleVat: true,
  },
  accountSettings: {},
  graphFilter: {
    checkCreditLimit: true,
    forecast: true,
    establishedBudget: true,
  },
  posts: [],
  liquidityForecast: {},
};

const mutations = {
  // Standard settings
  standardSettings(state, payload) {
    state.standardSettings = { ...state.standardSettings, ...payload };
  },
  posts(state, payload) {
    state.posts = payload;
  },
  appendDeviatingCheckCredit(state, payload) {
    state.standardSettings.deviatingCheckCredits.push(payload);
  },
  deleteDeviatingCheckCredit(state, index) {
    state.standardSettings.deviatingCheckCredits.splice(index, 1);
  },
  // Posts
  appendPost(state, payload) {
    state.posts.push(payload);
  },
  deletePost(state, id) {
    const index = state.posts.findIndex(post => post.id === id);
    if (index !== -1) {
      state.posts.splice(index, 1);
    }
  },
  editPost(state, updatedPost) {
    const index = state.posts.findIndex(post => post.id === updatedPost.id);
    if (index !== -1) {
      Vue.set(state.posts, index, updatedPost);
    }
  },
  accountSettings(state, payload) {
    state.accountSettings = payload;
  },
  liquidityForecast(state, payload) {
    state.liquidityForecast = payload;
  },
  graphFilter(state, payload) {
    state.graphFilter = payload;
  },
};

const getters = {
  liquidityForecast: state => {
    return state.liquidityForecast;
  },
  standardSettings: state => {
    return state.standardSettings;
  },
  accountSettings: state => {
    return state.accountSettings;
  },
  posts: state => {
    return state.posts;
  },
  graphFilter: state => {
    return state.graphFilter;
  },
};

const actions = {
  fetchLiquidityForecast: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/liquidity-forecast/' + params.companyId,
      params,
      'liquidityForecast'
    );
  },
  fetchLiquidityForecastTotal: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/total',
      params,
      'liquidityForecast'
    );
  },
  fetchAccountSettings: async (context, params) => {
    await fetch(
      context,
      'businessboard/liquidity-forecast/account-settings/' + params.companyId,
      'accountSettings'
    );
  },
  fetchGraphFilter: async (context, params) => {
    await fetch(
      context,
      'businessboard/liquidity-forecast/graph-filter-single/' +
        params.companyId,
      'graphFilter'
    );
  },
  fetchGraphFilterClub: async context => {
    await fetch(
      context,
      'businessboard/liquidity-forecast/graph-filter-single-club',
      'graphFilter'
    );
  },
  patchAccountSettings: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/account-settings/' + params.companyId,
      params,
      'accountSettings'
    );
  },
  patchGraphFilter: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/graph-filter-single/' +
        params.companyId,
      params,
      'graphFilter'
    );
  },
  patchGraphFilterClub: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/graph-filter-single-club',
      params,
      'graphFilter'
    );
  },
  fetchStandardSettings: async (context, params) => {
    await fetch(
      context,
      'businessboard/liquidity-forecast/standard-settings/' + params.companyId,
      'standardSettings'
    );
  },
  fetchNonRecurringPosts: async (context, params) => {
    await fetch(
      context,
      'businessboard/liquidity-forecast/non-recurring-posts/' +
        params.companyId,
      'posts'
    );
  },
  patchStandardSettings: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/standard-settings/' + params.companyId,
      context.state.standardSettings,
      'standardSettings'
    );
  },
  addCheckCredit({ commit }, checkCredit) {
    commit('appendDeviatingCheckCredit', checkCredit);
  },
  removeCheckCredit({ commit }, index) {
    commit('deleteDeviatingCheckCredit', index);
  },
  addPost: async (context, params) => {
    await post(
      context,
      'businessboard/liquidity-forecast/non-recurring-posts/' +
        params.companyId,
      params.post,
      'appendPost'
    );
  },
  editPost: async (context, { post }) => {
    await patch(
      context,
      `businessboard/liquidity-forecast/non-recurring-posts/${post.id}`,
      post,
      'editPost'
    );
  },
  removePost: async ({ commit }, index) => {
    let response = await Vue.axios.delete(
      `businessboard/liquidity-forecast/non-recurring-posts/${index}`
    );
    if (response.status === 200) {
      commit('deletePost', index);
    }
  },
  updateStandardSettings({ commit }, payload) {
    commit('updateStandardSettings', payload);
  },
  importVatCodes: async (context, params) => {
    try {
      let formData = new FormData();
      formData.append('file', params.file);

      let response = await Vue.axios.post(
        'businessboard/liquidity-forecast/import/' + params.companyId,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        EventBus.$emit('toast', {
          type: 'success',
          msg: 'Import success',
        });
      } else if (!response.data.success) {
        EventBus.$emit('toast', {
          type: 'error',
          msg: 'Import failed',
        });
      }

      return {
        success: response.success,
        status: response.status,
        resultCode: response.data.resultCode,
      };
    } catch {
      return false;
    }
  },
};

async function fetch(context, path, commit) {
  try {
    let response = await Vue.axios.get(path);
    if (response && response.data.success) {
      context.commit(commit, response.data.result);
    }
  } catch (error) {
    EventBus.$emit('toast', {
      type: 'error',
      msg: error.response.data.message,
      request: path,
    });
  }
}

async function post(context, path, body, commit) {
  let response = {};
  try {
    response = await Vue.axios.post(path, body);
    if (response && response.status === 200) {
      const data = response.data.result;
      context.commit(commit, data);
    }
  } catch (error) {
    EventBus.$emit('toast', {
      type: 'error',
      msg: createErrorMessage(`Unable to post ${path}`, error),
      request: path,
    });
  }
}

async function patch(context, path, body, commit) {
  try {
    let response = await Vue.axios.patch(path, body);
    if (response && response.status === 200) {
      context.commit(commit, response.data.result);
    }
  } catch (error) {
    EventBus.$emit('toast', {
      type: 'error',
      msg: createErrorMessage(`Unable to patch ${path}`, error),
      request: path,
    });
  }
}

function createErrorMessage(message, error) {
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message
  ) {
    return message + ' ' + error.response.data.message;
  }
  return message;
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
